import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ClipboardList, Edit2, Info, Loader2, Plus, Power, PowerOff, Search } from 'lucide-react';
import { adminDoa, type DoaResponsibilityRow, type CatalogScope } from '@/api/client';
import { ResponsibilityFormDialog } from './ResponsibilityFormDialog';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-16 — DoA responsibilities master-data console (platform_admin only).
 *
 * The canonical vocabulary that drives the columns of the DoA grid. Add a
 * new responsibility, edit any field on an existing one, or retire types
 * that are no longer in use. Changes propagate to every tenant immediately.
 *
 * Layout matches EisfMasterDataPage: stats row → search + show-inactive
 * toggle → table with row actions. Clicking a row opens the edit dialog.
 */

export function DoaResponsibilitiesPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isPlatform = user?.role === 'platform_admin' || user?.role === 'application_admin';
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState<DoaResponsibilityRow | null>(
    null,
  );
  const [scopeFilter, setScopeFilter] = useState<CatalogScope | undefined>(undefined);

  const listQ = useQuery({
    queryKey: ['adminDoa', 'responsibilities', { showInactive, scopeFilter }],
    queryFn: () =>
      adminDoa.listResponsibilities({ include_inactive: showInactive, scope: scopeFilter }),
  });

  const deactivateM = useMutation({
    mutationFn: (code: string) => adminDoa.deactivateResponsibility(code),
    onSuccess: (_, code) => {
      qc.invalidateQueries({ queryKey: ['adminDoa', 'responsibilities'] });
      toast.success(`Deactivated "${code}" — hidden from new grids`);
      setConfirmingDeactivate(null);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Deactivation failed'));
    },
  });

  const reactivateM = useMutation({
    mutationFn: (code: string) => adminDoa.updateResponsibility(code, { is_active: true }),
    onSuccess: (_, code) => {
      qc.invalidateQueries({ queryKey: ['adminDoa', 'responsibilities'] });
      toast.success(`Reactivated "${code}"`);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Reactivation failed'));
    },
  });

  const rows = useMemo(() => {
    const data = listQ.data?.responsibilities ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (r) =>
        r.label.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        r.definition.toLowerCase().includes(q),
    );
  }, [listQ.data, search]);

  const stats = listQ.data ?? { total: 0, active: 0, inactive: 0, responsibilities: [] };

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="DoA Responsibilities"
        subtitle="Canonical vocabulary of delegable tasks. Add or retire responsibilities; every tenant's DoA editor inherits immediately."
        actions={
          <button
            type="button"
            onClick={() => setCreatingNew(true)}
            className="inline-flex items-center gap-2 rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            <Plus className="h-4 w-4" />
            Add responsibility
          </button>
        }
      />

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard
          label="Total"
          value={stats.total}
          icon={<ClipboardList className="h-4 w-4 text-ink-muted" />}
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        />
        <StatCard
          label="Inactive"
          value={stats.inactive}
          icon={<PowerOff className="h-4 w-4 text-amber-600" />}
        />
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 rounded border border-surface-border bg-white px-3 h-10 flex-1 min-w-[260px] max-w-md">
          <Search className="h-4 w-4 text-ink-muted shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by label, code, or definition…"
            className="flex-1 bg-transparent outline-none text-sm text-ink-primary placeholder:text-ink-muted"
          />
        </div>
        {/* Feedback #17/#18/#6 - scope filter tabs */}
        <div className="inline-flex rounded border border-surface-border bg-white p-0.5">
          <ScopeTab
            active={scopeFilter === undefined}
            onClick={() => setScopeFilter(undefined)}
            label={isPlatform ? 'All scopes' : 'My view'}
          />
          <ScopeTab
            active={scopeFilter === 'platform'}
            onClick={() => setScopeFilter('platform')}
            label="Platform"
          />
          <ScopeTab
            active={scopeFilter === 'site'}
            onClick={() => setScopeFilter('site')}
            label={isPlatform ? 'Site' : 'My site'}
          />
          <ScopeTab
            active={scopeFilter === 'study'}
            onClick={() => setScopeFilter('study')}
            label="Study"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-ink-secondary cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4"
          />
          Show inactive
        </label>
      </div>

      {!isPlatform && (
        <div className="mt-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          You have <span className="font-semibold">site admin</span> access. Platform
          responsibilities are read-only; use the <span className="font-semibold">My site</span> or
          <span className="font-semibold">Study</span> tabs to add or edit your own catalog entries.
        </div>
      )}

      {listQ.isLoading && (
        <div className="mt-10 flex items-center justify-center gap-2 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading responsibilities…
        </div>
      )}

      {listQ.isError && (
        <div className={`mt-6 flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(listQ.error)}`}>
          {isPermissionError(listQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>
            {errorText(listQ.error, 'Failed to load responsibilities.')}
          </span>
        </div>
      )}

      {listQ.data && rows.length === 0 && (
        <div className="mt-10 rounded border border-surface-border bg-surface-muted/30 p-10 text-center text-sm text-ink-muted">
          {search
            ? 'No responsibilities match your search.'
            : 'No responsibilities in the catalog yet.'}
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-4 rounded border border-surface-border bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-surface-border bg-surface-muted/40 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-2.5 w-12 text-center">Order</th>
                <th className="px-4 py-2.5">Label</th>
                <th className="px-4 py-2.5">Code</th>
                <th className="px-4 py-2.5">Scope</th>
                <th className="px-4 py-2.5">State</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.responsibility_id}
                  className="border-b border-surface-border/40 last:border-0 hover:bg-surface-muted/30 cursor-pointer"
                  onClick={() => setEditingCode(r.code)}
                >
                  <td className="px-4 py-3 text-center font-mono text-xs text-ink-muted">
                    {r.sort_order}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-primary">{r.label}</div>
                    {r.definition && (
                      <div className="text-xs text-ink-muted truncate max-w-md mt-0.5">
                        {r.definition.slice(0, 140)}
                        {r.definition.length > 140 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">{r.code}</td>
                  <td className="px-4 py-3">
                    <ScopeBadge scope={r.scope ?? 'platform'} />
                  </td>
                  <td className="px-4 py-3">
                    {r.is_active ? (
                      <span className="inline-flex rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex rounded bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingCode(r.code)}
                        className="inline-flex items-center gap-1 rounded border border-surface-border px-2.5 py-1 text-xs font-medium text-ink-primary hover:bg-surface-muted/50"
                      >
                        <Edit2 className="h-3 w-3" /> Edit
                      </button>
                      {r.is_active ? (
                        <button
                          type="button"
                          onClick={() => setConfirmingDeactivate(r)}
                          className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                        >
                          <PowerOff className="h-3 w-3" /> Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={reactivateM.isPending}
                          onClick={() => reactivateM.mutate(r.code)}
                          className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
                        >
                          <Power className="h-3 w-3" /> Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingCode && (
        <ResponsibilityFormDialog
          mode="edit"
          code={editingCode}
          onClose={() => setEditingCode(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['adminDoa', 'responsibilities'] });
            setEditingCode(null);
          }}
        />
      )}

      {creatingNew && (
        <ResponsibilityFormDialog
          mode="create"
          onClose={() => setCreatingNew(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['adminDoa', 'responsibilities'] });
            setCreatingNew(false);
          }}
        />
      )}

      {confirmingDeactivate && (
        <DeactivateConfirmModal
          row={confirmingDeactivate}
          isPending={deactivateM.isPending}
          onCancel={() => setConfirmingDeactivate(null)}
          onConfirm={() => deactivateM.mutate(confirmingDeactivate.code)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="rounded border border-surface-border bg-white p-3">
      <div className="flex items-center justify-between text-xs text-ink-muted">
        <span>{label}</span>
        {icon}
      </div>
      <div className="mt-1 text-2xl font-semibold text-ink-primary">{value}</div>
    </div>
  );
}

function ScopeTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center rounded px-2.5 py-1 text-xs font-medium ${
        active ? 'bg-ink-primary text-white' : 'text-ink-secondary hover:bg-surface-muted/60'
      }`}
    >
      {label}
    </button>
  );
}

function ScopeBadge({ scope }: { scope: CatalogScope }) {
  if (scope === 'platform') {
    return (
      <span className="inline-flex rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium text-ink-secondary">
        platform
      </span>
    );
  }
  if (scope === 'site') {
    return (
      <span className="inline-flex rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
        site
      </span>
    );
  }
  return (
    <span className="inline-flex rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
      study
    </span>
  );
}

function DeactivateConfirmModal({
  row,
  isPending,
  onCancel,
  onConfirm,
}: {
  row: DoaResponsibilityRow;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden
      />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="space-y-2 min-w-0">
            <h3 className="text-sm font-semibold text-ink-primary">
              Deactivate this responsibility?
            </h3>
            <p className="text-xs text-ink-muted">
              <span className="font-medium text-ink-primary">{row.label}</span> will be hidden from
              new DoA grids. Existing junction rows pointing at it remain valid — historical DoA
              versions stay readable. Reactivation is one click away.
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center gap-1.5 rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:opacity-60"
          >
            {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            Deactivate
          </button>
        </div>
      </div>
    </div>
  );
}
